import React, { useState, useEffect, useRef } from 'react';
import { Megaphone, X, GripVertical } from 'lucide-react';
import { getLatestAnnouncement } from '../../../lib/announcementService';

const AnnouncementWidget = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const widgetRef = useRef(null);

  // Fetch announcement data from Firebase on component mount
  useEffect(() => {
    const loadAnnouncement = async () => {
      try {
        setLoading(true);
        const latestAnnouncement = await getLatestAnnouncement();
        
        if (latestAnnouncement) {
          setAnnouncement(latestAnnouncement);
        } else {
          // Fallback to placeholder if no Firebase data available
          setAnnouncement({
            title: "Park Maintenance Schedule Update",
            description: `
              <div>
                <p><strong>Important Notice:</strong> The underground river tour will undergo scheduled maintenance from January 20-22, 2026.</p>
                <br/>
                <p>During this period:</p>
                <ul style="list-style-type: disc; margin-left: 20px; margin-top: 10px;">
                  <li>Tours will be limited to morning slots only (8:00 AM - 12:00 PM)</li>
                  <li>Maximum capacity reduced to 50 visitors per day</li>
                  <li>Alternative activities available at the Visitor Center</li>
                </ul>
                <br/>
                <p>We apologize for any inconvenience. For rebooking assistance, please contact our visitor services team.</p>
                <br/>
                <p><em>Last updated: January 15, 2026</em></p>
              </div>
            `
          });
        }
      } catch (error) {
        console.error('Error loading announcement:', error);
        // Use fallback data on error
        setAnnouncement({
          title: "Park Maintenance Schedule Update",
          description: `
            <div>
              <p><strong>Important Notice:</strong> The underground river tour will undergo scheduled maintenance from January 20-22, 2026.</p>
              <br/>
              <p>During this period:</p>
              <ul style="list-style-type: disc; margin-left: 20px; margin-top: 10px;">
                <li>Tours will be limited to morning slots only (8:00 AM - 12:00 PM)</li>
                <li>Maximum capacity reduced to 50 visitors per day</li>
                <li>Alternative activities available at the Visitor Center</li>
              </ul>
              <br/>
              <p>We apologize for any inconvenience. For rebooking assistance, please contact our visitor services team.</p>
              <br/>
              <p><em>Last updated: January 15, 2026</em></p>
            </div>
          `
        });
      } finally {
        setLoading(false);
      }
    };

    loadAnnouncement();
  }, []);

  // Initialize position to bottom-right
  useEffect(() => {
    const positionBottomRight = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Mobile responsive positioning
      if (viewportWidth < 640) {
        // Mobile: smaller margin from edges
        setPosition({
          x: viewportWidth - 70, // 70px from right (60px widget + 10px margin)
          y: viewportHeight - 90  // 90px from bottom
        });
      } else {
        // Desktop: larger margin
        setPosition({
          x: viewportWidth - 90, // 90px from right (70px widget + 20px margin)
          y: viewportHeight - 110 // 110px from bottom
        });
      }
    };

    if (position?.x === null) {
      positionBottomRight();
    }

    window.addEventListener('resize', positionBottomRight);
    return () => window.removeEventListener('resize', positionBottomRight);
  }, [position?.x]);

  // Handle drag start
  const handleMouseDown = (e) => {
    if (e?.target?.closest('.drag-handle') && !isExpanded) {
      setIsDragging(true);
      setHasDragged(false);
      const rect = widgetRef?.current?.getBoundingClientRect();
      setDragOffset({
        x: e?.clientX - rect?.left,
        y: e?.clientY - rect?.top
      });
    }
  };

  const handleTouchStart = (e) => {
    if (e?.target?.closest('.drag-handle') && !isExpanded) {
      setIsDragging(true);
      setHasDragged(false);
      const touch = e?.touches?.[0];
      const rect = widgetRef?.current?.getBoundingClientRect();
      setDragOffset({
        x: touch?.clientX - rect?.left,
        y: touch?.clientY - rect?.top
      });
    }
  };

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && widgetRef?.current) {
        setHasDragged(true);
        const newX = e?.clientX - dragOffset?.x;
        const newY = e?.clientY - dragOffset?.y;
        
        const widgetWidth = widgetRef?.current?.offsetWidth;
        const widgetHeight = widgetRef?.current?.offsetHeight;
        const maxX = window.innerWidth - widgetWidth;
        const maxY = window.innerHeight - widgetHeight;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleTouchMove = (e) => {
      if (isDragging && widgetRef?.current) {
        setHasDragged(true);
        const touch = e?.touches?.[0];
        const newX = touch?.clientX - dragOffset?.x;
        const newY = touch?.clientY - dragOffset?.y;
        
        const widgetWidth = widgetRef?.current?.offsetWidth;
        const widgetHeight = widgetRef?.current?.offsetHeight;
        const maxX = window.innerWidth - widgetWidth;
        const maxY = window.innerHeight - widgetHeight;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragOffset]);

  // Handle double-click to open details
  const handleDoubleClick = () => {
    if (!hasDragged && !isExpanded) {
      setIsExpanded(true);
    }
  };

  // Don't render widget if loading or no announcement
  if (loading || !announcement) {
    return null;
  }

  return (
    <>
      {/* Collapsed Widget - Floating Button */}
      {!isExpanded && (
        <div
          ref={widgetRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onDoubleClick={handleDoubleClick}
          style={{
            left: position?.x !== null ? `${position?.x}px` : '50%',
            top: position?.y !== null ? `${position?.y}px` : '50%',
            transform: position?.x === null ? 'translate(-50%, -50%)' : 'none',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
          className="fixed z-30 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl shadow-2xl transition-all hover:scale-105 group"
        >
          <div className="drag-handle flex items-center gap-2 px-4 py-3 sm:px-5 sm:py-4">
            <GripVertical className="h-4 w-4 sm:h-5 sm:w-5 opacity-70" />
            <Megaphone className="h-5 w-5 sm:h-6 sm:w-6" />
            <span className="text-xs sm:text-sm font-semibold max-w-[120px] sm:max-w-[180px] truncate">
              {announcement?.title}
            </span>
          </div>
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center animate-pulse">
            1
          </div>
        </div>
      )}

      {/* Expanded Modal */}
      {isExpanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-blue-600 text-white px-4 sm:px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Megaphone className="h-5 w-5 sm:h-6 sm:w-6" />
                <h3 className="text-base sm:text-lg font-bold">Announcement</h3>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
                aria-label="Close announcement"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">
                {announcement?.title}
              </h4>
              <div 
                className="text-sm sm:text-base text-gray-700 dark:text-gray-300 prose prose-sm sm:prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: announcement?.description }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnnouncementWidget;